import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Stack,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close,
  CameraAlt,
  Download,
  Refresh,
  Visibility,
  PhotoCamera,
  Image,
} from '@mui/icons-material';

import { api } from '../../services/api';
import type { Device } from '../../types';

interface DeviceScreenshotsDialogProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
}

interface Screenshot {
  filename: string;
  file_size: number;
  quality: number;
  created_at: string;
  base64_data?: string;
  mime_type?: string;
}

const DeviceScreenshotsDialog: React.FC<DeviceScreenshotsDialogProps> = ({
  open,
  onClose,
  device,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // State для создания снимка
  const [quality, setQuality] = useState(80);
  const [width, setWidth] = useState<number | ''>('');
  const [height, setHeight] = useState<number | ''>('');
  const [returnBase64, setReturnBase64] = useState(true);
  const [createdScreenshot, setCreatedScreenshot] = useState<{
    filename: string;
    base64_data?: string;
    mime_type?: string;
  } | null>(null);

  // Query для получения списка снимков
  const {
    data: screenshotsData,
    isLoading: screenshotsLoading,
    error: screenshotsError,
    refetch: refetchScreenshots,
  } = useQuery({
    queryKey: ['device-screenshots', device?.id],
    queryFn: () => {
      if (!device) return null;
      return api.getScreenshots(device.id, { limit: 20 });
    },
    enabled: open && !!device,
  });

  // Mutation для создания снимка
  const takeScreenshotMutation = useMutation({
    mutationFn: () => {
      if (!device) throw new Error('No device selected');
      
      const params: any = {
        quality,
        return_base64: returnBase64,
      };
      
      if (width) params.width = Number(width);
      if (height) params.height = Number(height);
      
      return api.takeScreenshot(device.id, params);
    },
    onSuccess: (data) => {
      setCreatedScreenshot({
        filename: data.filename,
        base64_data: data.base64_data,
        mime_type: data.mime_type,
      });
      // Обновляем список снимков
      refetchScreenshots();
    },
  });

  const handleTakeScreenshot = () => {
    setCreatedScreenshot(null);
    takeScreenshotMutation.mutate();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const findScreenshotData = (filename: string): { filename: string; base64_data?: string; mime_type?: string } | null => {
    if (createdScreenshot?.base64_data && createdScreenshot.filename === filename) {
      return createdScreenshot;
    }
    const screenshot = screenshotsData?.screenshots?.find((item: Screenshot) => item.filename === filename);
    return screenshot || null;
  };

  const dataUrlForScreenshot = (filename: string): string | null => {
    const screenshot = findScreenshotData(filename);
    if (!screenshot?.base64_data) return null;
    return `data:${screenshot.mime_type || 'image/png'};base64,${screenshot.base64_data}`;
  };

  const downloadScreenshot = (filename: string) => {
    const dataUrl = dataUrlForScreenshot(filename);
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const viewScreenshot = (filename: string) => {
    const dataUrl = dataUrlForScreenshot(filename);
    if (!dataUrl) return;

    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head><title>${filename}</title></head>
          <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f0f0f0;">
            <img src="${dataUrl}" style="max-width:100%;max-height:100%;object-fit:contain;" alt="${filename}" />
          </body>
        </html>
      `);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PhotoCamera color="primary" />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {t('devices.screenshots.title')} - {device?.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Создание снимка */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('devices.screenshots.takeScreenshot')}
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography gutterBottom>
                  {t('devices.screenshots.quality')}: {quality}%
                </Typography>
                <Slider
                  value={quality}
                  onChange={(_, value) => setQuality(value as number)}
                  min={10}
                  max={100}
                  step={10}
                  marks
                  valueLabelDisplay="auto"
                />
              </Grid>
              
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('devices.screenshots.width')}
                  value={width}
                  onChange={(e) => setWidth(e.target.value ? Number(e.target.value) : '')}
                  inputProps={{ min: 100, max: 4096 }}
                />
              </Grid>
              
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('devices.screenshots.height')}
                  value={height}
                  onChange={(e) => setHeight(e.target.value ? Number(e.target.value) : '')}
                  inputProps={{ min: 100, max: 4096 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={returnBase64}
                      onChange={(e) => setReturnBase64(e.target.checked)}
                    />
                  }
                  label={t('devices.screenshots.returnBase64')}
                />
              </Grid>
            </Grid>
          </CardContent>
          
          <CardActions>
            <Button
              variant="contained"
              startIcon={<CameraAlt />}
              onClick={handleTakeScreenshot}
              disabled={takeScreenshotMutation.isPending}
            >
              {takeScreenshotMutation.isPending ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  {t('devices.screenshots.loading')}
                </>
              ) : (
                t('devices.screenshots.takeScreenshot')
              )}
            </Button>
            
            <Tooltip title={t('common.refresh')}>
              <IconButton onClick={refetchScreenshots} disabled={screenshotsLoading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </CardActions>
        </Card>

        {/* Результат создания снимка */}
        {takeScreenshotMutation.isSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('devices.screenshots.success')}
          </Alert>
        )}

        {takeScreenshotMutation.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('devices.screenshots.error')}: {takeScreenshotMutation.error.message}
          </Alert>
        )}

        {/* Превью созданного снимка */}
        {createdScreenshot?.base64_data && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.preview')}
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={`data:${createdScreenshot.mime_type};base64,${createdScreenshot.base64_data}`}
                  alt={createdScreenshot.filename}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Список снимков */}
        <Typography variant="h6" gutterBottom>
          {t('devices.screenshots.viewScreenshots')}
        </Typography>

        {screenshotsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('common.error')}: {screenshotsError.message}
          </Alert>
        )}

        {screenshotsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : screenshotsData?.screenshots?.length ? (
          <>
            {/* Статистика */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Chip
                icon={<Image />}
                label={`${screenshotsData.total_count} ${t('devices.screenshots.title').toLowerCase()}`}
                variant="outlined"
              />
              <Chip
                label={`${formatFileSize(screenshotsData.total_size)}`}
                variant="outlined"
              />
            </Stack>

            {/* Сетка снимков */}
            <Grid container spacing={2}>
              {screenshotsData.screenshots.map((screenshot, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card>
                    <CardContent sx={{ pb: 1 }}>
                      <Typography variant="body2" noWrap title={screenshot.filename}>
                        {screenshot.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(screenshot.file_size)} • {screenshot.quality}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatDateTime(screenshot.created_at)}
                      </Typography>
                    </CardContent>
                    
                    <CardActions size="small">
                      <Tooltip title={dataUrlForScreenshot(screenshot.filename) ? t('devices.screenshots.view') : 'Устройство вернуло запись снимка без данных изображения'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => viewScreenshot(screenshot.filename)}
                            disabled={!dataUrlForScreenshot(screenshot.filename)}
                          >
                            <Visibility />
                          </IconButton>
                        </span>
                      </Tooltip>
                      
                      <Tooltip title={dataUrlForScreenshot(screenshot.filename) ? t('devices.screenshots.download') : 'Скачать нельзя: нет base64-данных снимка'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => downloadScreenshot(screenshot.filename)}
                            disabled={!dataUrlForScreenshot(screenshot.filename)}
                          >
                            <Download />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('devices.screenshots.noScreenshots')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceScreenshotsDialog;
